<template>
  <div class="page">
    <h1>Einstellungen</h1>

    <p v-if="!Benutzer.Angemeldet" class="hint-message">
      Nicht angemeldet – Änderungen gelten nur für diese Sitzung und werden nicht gespeichert.
    </p>
    <p v-else-if="Speicherstatus" class="hint-message success">{{ Speicherstatus }}</p>

    <section class="glas-karte settings-karte">
      <h2>Darstellung</h2>
      <div class="segment-schalter">
        <button
          type="button"
          class="segment-option"
          :class="{ aktiv: theme.mode === 'light' }"
          @click="ModusUndSpeichern('light')"
        >
          <font-awesome-icon icon="fa-solid fa-sun" /> Light
        </button>
        <button
          type="button"
          class="segment-option"
          :class="{ aktiv: theme.mode === 'dark' }"
          @click="ModusUndSpeichern('dark')"
        >
          <font-awesome-icon icon="fa-solid fa-moon" /> Dark
        </button>
      </div>
    </section>

    <section class="glas-karte settings-karte">
      <h2>Akzentfarbe</h2>
      <div class="farb-reihe">
        <button
          v-for="Farbe in AkzentFarben"
          :key="Farbe.Wert"
          type="button"
          class="farb-swatch"
          :class="{ 'farb-swatch--aktiv': theme.accent === Farbe.Wert }"
          :style="{ background: Farbe.Wert }"
          :aria-label="Farbe.Name"
          :title="Farbe.Name"
          @click="AkzentUndSpeichern(Farbe.Wert)"
        >
          <font-awesome-icon v-if="theme.accent === Farbe.Wert" icon="fa-solid fa-check" />
        </button>
      </div>
    </section>

    <section v-if="Benutzer.Angemeldet" class="glas-karte settings-karte">
      <h2>Benutzerdaten</h2>
      <form class="konto-form" @submit.prevent>
        <label for="konto-name">Name</label>
        <div class="input-field">
          <font-awesome-icon icon="fa-solid fa-user" />
          <input id="konto-name" v-model="Konto.Name" type="text" placeholder="Name" />
        </div>

        <label for="konto-email">E-Mail-Adresse</label>
        <div class="input-field">
          <font-awesome-icon icon="fa-solid fa-envelope" />
          <input id="konto-email" v-model="Konto.EMail" type="email" placeholder="E-Mail-Adresse" autocomplete="username" />
        </div>

        <label for="konto-passwort">Neues Passwort</label>
        <div class="input-field">
          <font-awesome-icon icon="fa-solid fa-lock" />
          <input
            id="konto-passwort"
            v-model="Konto.Passwort"
            type="password"
            placeholder="Nur ausfüllen, um es zu ändern"
            autocomplete="new-password"
          />
        </div>

        <label for="konto-passwort-bestaetigen">Passwort bestätigen</label>
        <div class="input-field">
          <font-awesome-icon icon="fa-solid fa-lock" />
          <input
            id="konto-passwort-bestaetigen"
            v-model="Konto.PasswortBestaetigen"
            type="password"
            placeholder="Passwort bestätigen"
            autocomplete="new-password"
          />
        </div>

        <p v-if="KontoFehlermeldung" class="error-message">{{ KontoFehlermeldung }}</p>
        <p v-else-if="KontoStatus" class="hint-message success">{{ KontoStatus }}</p>

        <div class="button-row">
          <button type="button" class="btn btn-primary" @click="KontoSpeichern">Speichern</button>
          <button type="button" class="btn btn-secondary" @click="Abbrechen">Abbrechen</button>
        </div>
      </form>
    </section>

    <section class="glas-karte settings-karte about-karte">
      <h2>Über TopfKucker</h2>
      <div class="about-zeile">
        <span class="about-logo">
          <font-awesome-icon icon="fa-solid fa-utensils" />
        </span>
        <div class="about-text">
          <span class="about-name">TopfKucker</span>
          <span class="about-version">Version {{ Version }}</span>
          <span class="about-copyright">© Andreas Neubert · Fürstenfeldbruck</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { theme, setMode, setAccent, AkzentFarben, ThemeProfilDaten } from '../composables/useTheme'
import { Benutzer, ProfilSpeichern, AktuellenBenutzerLaden, KontoAktualisieren } from '../composables/useBenutzer'

export default {
  name: 'EinstellungenView',
  data() {
    return {
      theme,
      Benutzer,
      AkzentFarben,
      Version: process.env.VUE_APP_VERSION || '?',
      Speicherstatus: '',
      Konto: {
        Name: '',
        EMail: '',
        Passwort: '',
        PasswortBestaetigen: ''
      },
      KontoOriginal: {
        Name: '',
        EMail: ''
      },
      KontoFehlermeldung: '',
      KontoStatus: ''
    }
  },
  async mounted() {
    if (this.Benutzer.Angemeldet) {
      await AktuellenBenutzerLaden()
      this.KontoOriginal.Name = this.Benutzer.Name
      this.KontoOriginal.EMail = this.Benutzer.EMail
      this.Konto.Name = this.Benutzer.Name
      this.Konto.EMail = this.Benutzer.EMail
    }
  },
  methods: {
    ModusUndSpeichern(mode) {
      setMode(mode)
      this.Speichern()
    },
    AkzentUndSpeichern(Wert) {
      setAccent(Wert)
      this.Speichern()
    },
    async Speichern() {
      if (!this.Benutzer.Angemeldet) {
        return
      }
      try {
        await ProfilSpeichern(ThemeProfilDaten())
        this.Speicherstatus = 'Gespeichert'
        setTimeout(() => { this.Speicherstatus = '' }, 2000)
      } catch (Fehler) {
        this.Speicherstatus = 'Fehler beim Speichern: ' + Fehler.message
      }
    },
    async KontoSpeichern() {
      this.KontoFehlermeldung = ''
      this.KontoStatus = ''

      if (this.Konto.Passwort !== this.Konto.PasswortBestaetigen) {
        this.KontoFehlermeldung = 'Die Passwörter stimmen nicht überein.'
        return
      }

      const Aenderungen = {}
      if (this.Konto.Name !== this.KontoOriginal.Name) {
        Aenderungen.Name = this.Konto.Name
      }
      if (this.Konto.EMail !== this.KontoOriginal.EMail) {
        Aenderungen.EMail = this.Konto.EMail
      }
      if (this.Konto.Passwort !== '') {
        Aenderungen.Passwort = this.Konto.Passwort
      }

      if (Object.keys(Aenderungen).length === 0) {
        this.KontoStatus = 'Keine Änderungen.'
        return
      }

      try {
        await KontoAktualisieren(Aenderungen)
        if (Aenderungen.Name !== undefined) {
          this.KontoOriginal.Name = Aenderungen.Name
        }
        if (Aenderungen.EMail !== undefined) {
          this.KontoOriginal.EMail = Aenderungen.EMail
        }
        this.Konto.Passwort = ''
        this.Konto.PasswortBestaetigen = ''
        this.KontoStatus = 'Gespeichert.'
      } catch (Fehler) {
        this.KontoFehlermeldung = Fehler.message
      }
    },
    Abbrechen() {
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
.settings-karte {
  margin-top: 20px;
  max-width: 460px;
}

.settings-karte h2 {
  margin: 0 0 14px;
  font-size: 1rem;
  font-weight: 650;
}

.farb-reihe {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.farb-swatch {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  color: #fff;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.18s ease, box-shadow 0.25s ease;
}

.farb-swatch:hover {
  transform: scale(1.12);
}

.farb-swatch--aktiv {
  box-shadow: 0 0 0 3px var(--color-bg), 0 0 0 6px var(--color-primary);
}

.konto-form {
  display: flex;
  flex-direction: column;
}

.konto-form label {
  margin-top: 14px;
  margin-bottom: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-text-muted);
}

.konto-form label:first-child {
  margin-top: 0;
}

.about-karte {
  margin-bottom: 24px;
}

.about-zeile {
  display: flex;
  align-items: center;
  gap: 14px;
}

.about-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--color-primary), var(--akzent-tief));
  color: #fff;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.about-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.about-name {
  font-weight: 700;
  font-size: 0.98rem;
}

.about-version {
  font-size: 0.85rem;
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
}

.about-copyright {
  font-size: 0.8rem;
  color: var(--color-text-muted);
}
</style>
